import SentryIcon from "@/assets/sentry-icon.svg";
import SlackIcon from "@/assets/slack-icon.svg";
import type { Skill } from "@/features/skills/api/skills-api";
import { cn } from "@/lib/utils";
import {
  Activity,
  BookOpen,
  ClipboardCheck,
  Github,
  HelpCircle,
  LayoutList,
  ListOrdered,
  ScrollText,
  Search,
  Settings2,
  ShieldAlert,
  Zap,
} from "lucide-react";
import { useEffect, useRef } from "react";

interface SlashCommandContentProps {
  skills: Skill[];
  selectedIndex: number;
  onSelect: (skill: Skill) => void;
}

function KairoIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="4" fill="#FFCBAC" />
      <polygon
        fill="#002666"
        points="8.22851562 7.07861328 8.22851562 25.0784799 12.1383079 25.0784799 12.1383079 19.765647 14.8493058 17.1539676 20.3459494 25.0784799 25.2285156 25.0784799 17.9368659 14.6041598 25.2285156 7.07861328 20.3459494 7.07861328 12.1383079 15.2468788 12.1383079 7.07861328"
      />
    </svg>
  );
}

function getSkillIcon(slug: string) {
  switch (slug) {
    case "slack":
      return (
        <div className="border-border mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border bg-white">
          <SlackIcon className="h-3.5 w-3.5" />
        </div>
      );
    case "github":
      return (
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary">
          <Github className="h-3 w-3 text-primary-foreground" />
        </div>
      );
    case "sentry":
      // Keep the literal purple: --brand-sentry turns white in dark mode, which would
      // make this chip white-on-white. The filter paints the mark white either way.
      return (
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-[#362D59]">
          <SentryIcon className="h-3 w-3 brightness-0 invert" />
        </div>
      );
    case "kairo":
      return (
        <div className="mt-0.5 h-5 w-5 shrink-0 overflow-hidden rounded">
          <KairoIcon className="h-full w-full" />
        </div>
      );
    case "clarify":
      return (
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-amber-100 dark:bg-amber-900/30">
          <HelpCircle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
        </div>
      );
    case "detailed-plan":
      return (
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-indigo-100 dark:bg-indigo-900/30">
          <ListOrdered className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />
        </div>
      );
    case "adversarial-review":
      return (
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-red-100 dark:bg-red-900/30">
          <ShieldAlert className="h-3 w-3 text-red-600 dark:text-red-400" />
        </div>
      );
    case "project-state":
      return (
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-emerald-100 dark:bg-emerald-900/30">
          <Activity className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
        </div>
      );
    case "assess-feature":
      return (
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-green-100 dark:bg-green-900/30">
          <ClipboardCheck className="h-3 w-3 text-green-600 dark:text-green-400" />
        </div>
      );
    case "release-notes":
      return (
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-teal-100 dark:bg-teal-900/30">
          <ScrollText className="h-3 w-3 text-teal-600 dark:text-teal-400" />
        </div>
      );
    case "kairo-list":
      return (
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-violet-100 dark:bg-violet-900/30">
          <LayoutList className="h-3 w-3 text-violet-600 dark:text-violet-400" />
        </div>
      );
    case "kairo-search":
      return (
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-violet-100 dark:bg-violet-900/30">
          <Search className="h-3 w-3 text-violet-600 dark:text-violet-400" />
        </div>
      );
    case "kairo-knowledge":
      return (
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-violet-100 dark:bg-violet-900/30">
          <BookOpen className="h-3 w-3 text-violet-600 dark:text-violet-400" />
        </div>
      );
    case "configure-github":
    case "configure-sentry":
    case "configure-slack":
      return (
        <div className="bg-muted mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded">
          <Settings2 className="text-muted-foreground h-3 w-3" />
        </div>
      );
    default:
      return (
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-violet-100 dark:bg-violet-900/30">
          <Zap className="h-3 w-3 text-violet-700 dark:text-violet-400" />
        </div>
      );
  }
}

export function SlashCommandContent({ skills, selectedIndex, onSelect }: SlashCommandContentProps) {
  // One ref slot per skill, keyed by flat order (integration first, workflow second)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Mirror diceui's useListHighlighting: scroll selected item into view on
  // keyboard navigation.
  useEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  if (skills.length === 0) {
    return (
      <div className="text-muted-foreground px-2 py-3 text-center text-sm">
        No matching commands
      </div>
    );
  }

  const indexMap = new Map(skills.map((s, i) => [s.slug, i]));

  return (
    <div className="p-1">
      <p className="px-0.5 py-1.5 text-sm font-semibold">Skills</p>
      {skills.map((skill) => {
        const idx = indexMap.get(skill.slug) ?? 0;
        return (
          <button
            key={skill.slug}
            ref={(el) => {
              itemRefs.current[idx] = el;
            }}
            type="button"
            className={cn(
              "flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none",
              idx === selectedIndex
                ? "bg-accent text-accent-foreground"
                : "hover:bg-accent hover:text-accent-foreground",
            )}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(skill)}
          >
            {getSkillIcon(skill.slug)}
            <div className="flex flex-col">
              <span className="font-medium">/{skill.slug}</span>
              {/* CR-100: the catalog endpoint already strips routing hints and
                  may leave nothing; an empty line would only add height. */}
              {skill.description ? (
                <span
                  data-testid="slash-skill-description"
                  className="text-muted-foreground text-xs"
                >
                  {skill.description}
                </span>
              ) : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}
